/**
 * Batch tools feature module (W2-11 batch-tools; DESIGN.md §4.14, §5.4, §5.6.5, §5.8; WORKPACKAGES Appendix B:
 * `tools.distribute|clone|fill|path|bake|flatten|stagger`, `transform.*`, `segments.extrapolate|join|reverse|link|
 * unlink|selectLinked|splitRpe|bake`, `select.*`, `edit.pasteSpecial`).
 *
 * Provides:
 *  - generator side sheets (non-modal, live ghosts): DIALOG.distribute, clone, fill, motionPath, bake, flatten;
 *  - dialogs DIALOG.pasteSpecial (returns the choice) and DIALOG.transform;
 *  - the commands above, in the Tools menu, the context menus of the lane, motion columns, curve editor and preview,
 *    the Edit menu and the palette; keys: Shift+S (select similar), Ctrl+Shift+V (paste special); RPE preset adds
 *    Ctrl+F / Ctrl+G (Fill between: pick the start / end note, CURVE-01);
 *  - the motion-path preview overlay (path drawn green -> red while the sheet is open);
 *  - settings rows `ext.batch.bakeDensity`, `ext.batch.pathPerBeat` (editors that show the default the tools use
 *    until the user changes it) and help topics;
 *  - at startup, the context-menu beat snapshot (contextBeat.ts) used by Range start / end.
 */

import { DIALOG } from '../../app/dialogs/types';
import type { FeatureModule, HelpTopicDef, KeyBinding, SettingDef } from '../../app/registry/types';
import { m } from './batch.messages';
import { DEFAULT_BAKE_DENSITY, DEFAULT_PATH_PER_BEAT, EXT } from './batchContext';
import { extNumberEditor } from './BatchSettingRows';
import { installContextBeat } from './contextBeat';
import { pathOverlayProvider, subscribePathPreview } from './pathOverlay';
import { PREVIEW_FRAME_ID } from '../../render/types';
import { PasteSpecialDialog } from './PasteSpecialDialog';
import { SEGMENT_COMMANDS } from './segmentOps';
import { SELECT_COMMANDS } from './selectCommands';
import { sheetOpen } from './sheetRegistry';
import { BakeSheet } from './sheets/BakeSheet';
import { CloneSheet } from './sheets/CloneSheet';
import { DistributeSheet } from './sheets/DistributeSheet';
import { FillSheet } from './sheets/FillSheet';
import { FlattenSheet } from './sheets/FlattenSheet';
import { MotionPathSheet } from './sheets/MotionPathSheet';
import { TOOL_COMMANDS } from './toolCommands';
import { TransformDialog } from './TransformDialog';
import { TRANSFORM_COMMANDS } from './transformCommands';

export const BATCH_COMMANDS = [...TOOL_COMMANDS, ...SEGMENT_COMMANDS, ...SELECT_COMMANDS, ...TRANSFORM_COMMANDS];

/**
 * Escape closes the open tool sheet from anywhere (UX-038), unless something more specific uses it: a hold or segment
 * being placed, the paste ghost, a drag, a text field or a focused dialog (the sheet itself handles Escape there).
 */
export const SHEET_OPEN_ATOM = 'batch.sheetOpen';
const CLOSE_SHEET_WHEN = `${SHEET_OPEN_ATOM} && !dialog && !textInput && !gesture && !placing.hold && !placing.segment && !paste.ghost`;

/**
 * Escape with a selection and nothing more specific (placement, ghost, drag, field, sheet, focused dialog): clear it.
 * One condition less than CLOSE_SHEET_WHEN, so an open sheet closes first.
 */
const CLEAR_SELECTION_WHEN = 'sel.any && !textInput && !gesture && !placing.hold && !placing.segment && !paste.ghost';

const SHARED_KEYS: readonly KeyBinding[] = [
  { chord: 'Escape', command: 'tools.closeSheet', when: CLOSE_SHEET_WHEN },
  { chord: 'Escape', command: 'select.none', when: CLEAR_SELECTION_WHEN },
  { chord: 'Shift+KeyS', command: 'select.similar', when: 'sel.any && !textInput' },
  { chord: 'Ctrl+Shift+KeyV', command: 'edit.pasteSpecial', when: 'project.open && !textInput' },
];

export const BATCH_KEYBINDINGS: Readonly<Record<'default' | 'rpe', readonly KeyBinding[]>> = {
  default: SHARED_KEYS,
  rpe: [
    ...SHARED_KEYS,
    { chord: 'Ctrl+KeyF', command: 'tools.fill', args: { pick: 'head' }, when: 'sel.notes && !textInput' },
    { chord: 'Ctrl+KeyG', command: 'tools.fill', args: { pick: 'tail' }, when: 'sel.notes && !textInput' },
  ],
};

const SETTINGS: readonly SettingDef[] = [
  {
    path: 'ext.batch.bakeDensity',
    title: m.settingBakeDensity,
    description: m.settingBakeDensityHelp,
    level: 'full',
    kind: 'custom',
    component: extNumberEditor({
      key: EXT.bakeDensity,
      def: DEFAULT_BAKE_DENSITY,
      min: 1,
      max: 64,
      title: m.settingBakeDensity,
    }),
    min: 1,
    max: 64,
    step: 1,
  },
  {
    path: 'ext.batch.pathPerBeat',
    title: m.settingPathPerBeat,
    description: m.settingPathPerBeatHelp,
    level: 'full',
    kind: 'custom',
    component: extNumberEditor({
      key: EXT.pathPerBeat,
      def: DEFAULT_PATH_PER_BEAT,
      min: 1,
      max: 1920,
      title: m.settingPathPerBeat,
    }),
    min: 1,
    max: 1920,
    step: 1,
  },
];

const HELP: readonly HelpTopicDef[] = [
  { id: 'batch.tools', title: m.helpToolsTitle, body: m.helpToolsBody, level: 'guided' },
  { id: 'batch.distribute', title: m.helpDistributeTitle, body: m.helpDistributeBody, level: 'guided' },
  { id: 'batch.path', title: m.helpPathTitle, body: m.helpPathBody, level: 'full' },
  { id: 'batch.layers', title: m.helpFlattenTitle, body: m.helpFlattenBody, level: 'full' },
];

/** Defaults the settings rows start from (the tools read the same values). */
export const BATCH_DEFAULTS = { bakeDensity: DEFAULT_BAKE_DENSITY, pathPerBeat: DEFAULT_PATH_PER_BEAT } as const;

const batchModule = {
  id: 'batch',
  commands: BATCH_COMMANDS,
  keybindings: BATCH_KEYBINDINGS,
  dialogs: [
    { id: DIALOG.distribute, kind: 'sheet', component: DistributeSheet },
    { id: DIALOG.clone, kind: 'sheet', component: CloneSheet },
    { id: DIALOG.fill, kind: 'sheet', component: FillSheet },
    { id: DIALOG.motionPath, kind: 'sheet', component: MotionPathSheet },
    { id: DIALOG.bake, kind: 'sheet', component: BakeSheet },
    { id: DIALOG.flatten, kind: 'sheet', component: FlattenSheet },
    { id: DIALOG.pasteSpecial, kind: 'modal', component: PasteSpecialDialog },
    { id: DIALOG.transform, kind: 'modal', component: TransformDialog },
  ],
  previewOverlays: () => [pathOverlayProvider()],
  whenAtoms: () => ({ [SHEET_OPEN_ATOM]: sheetOpen }),
  settings: SETTINGS,
  helpTopics: HELP,
  init: (services) => {
    const offBeat = installContextBeat(services);
    // A new or cleared motion path span redraws the preview overlays even without a document change (CR-W2-11-5).
    const offPath = subscribePathPreview(() => {
      services.scheduler.request(PREVIEW_FRAME_ID);
    });
    return () => {
      offPath();
      offBeat();
    };
  },
} satisfies FeatureModule;

export default batchModule;
